import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";


import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";

import styles from "./TableBookingRestaurantSettings.module.css";

import Container from "../../../components/Container";
import SettingTextField from "../../../components/SettingTextField";
import AlertMsg from "../../../components/AlertMsg";
import Loader from "../../../components/Loader";
import RestaurantHeader from "../../../components/RestaurantHeader";
import TableBookingToggle from "./TableBookingToggle";
import BookingAvailabilityWindows from "../../../components/BookingAvailabilityWindows";
import { 
  tableBookingRestaurantSettingsService,
  tableBookingService
} from "../../../api/useQueryHooks";

// Import constants, schemas, and utils
import { 
  DEFAULT_BASIC_SETTINGS,
  DEFAULT_RESTAURANT_SETTINGS,
  BOOKING_TYPES
} from "./constants";
import { tableBookingToggleSchema, tableBookingRestaurantSettingsSchema } from "./schemas";
import { QUERY_KEYS } from "../../../api/QueryKeys";

const TableBookingRestaurantSettings = () => {
  const navigate = useNavigate();
  const { rest_id: restaurantId } = useParams();
  const queryClient = useQueryClient();

  const [msgAlert, setMsgAlert] = useState({
    open: false,
    message: "",
    msgType: "error",
  });

  // Get booking type from the basic settings

  // Basic Settings Form
  const {
    control: basicControl,
    handleSubmit: handleBasicSubmit,
    reset: resetBasic,
    watch: watchBasic,
    formState: { errors: basicErrors },
  } = useForm({
    resolver: zodResolver(tableBookingToggleSchema),
    mode: "onChange",
    defaultValues: {
      ...DEFAULT_BASIC_SETTINGS,
    },
  });

  // Restaurant Settings Form
  const {
    control: restaurantControl,
    handleSubmit: handleRestaurantSubmit,
    reset: resetRestaurant,
    watch: watchRestaurant,
    formState: { errors: restaurantErrors },
  } = useForm({
    resolver: zodResolver(tableBookingRestaurantSettingsSchema),
    mode: "onChange",
    defaultValues: {
      ...DEFAULT_RESTAURANT_SETTINGS,
    },
  });

  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({
    control: restaurantControl,
    name: "restaurantEmails",
  });

  const {
    fields: phoneFields,
    append: appendPhone,
    remove: removePhone,
  } = useFieldArray({
    control: restaurantControl,
    name: "restaurantPhones",
  });

  const {
    fields: priceConfigFields,
    append: appendPriceConfig,
    remove: removePriceConfig,
  } = useFieldArray({
    control: restaurantControl,
    name: "deposit.0.priceConfiguration",
  });



  // Query to fetch table booking basic settings to get booking type
  const {
    data: basicSettings,
  } = tableBookingService.useGetTableBookingSettings(restaurantId);

  // Query to fetch table booking restaurant settings
  const {
    data: settings,
    isLoading: isLoadingSettings,
    error: settingsError,
  } = tableBookingRestaurantSettingsService.useGetTableBookingRestaurantSettings(restaurantId);

  // Mutation to update table booking basic settings
  const {
    mutate: updateBasicSettings,
    isPending: isUpdatingBasic,
  } = tableBookingService.useUpdateTableBookingSettings(
    (response) => {
       queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.tableBookingOptions, restaurantId],
      });
      // Show success message for basic settings update
      setMsgAlert({
        open: true,
        message: response.msg || "Basic settings updated successfully!",
        msgType: "success",
      });
      resetBasic(undefined, { keepValues: true });
    },
    (error) => {
      setMsgAlert({
        open: true,
        message: error.message || "Failed to update basic settings",
        msgType: "error",
      });
    }
  );

  // Mutation to create/update table booking restaurant settings
  const {
    mutate: createSettings,
    isPending: isCreating,
  } = tableBookingRestaurantSettingsService.useCreateTableBookingRestaurantSettings(
    (response) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.tableBookingRestaurantSettings, restaurantId],
      });
      setMsgAlert({
        open: true,
        message: response.msg || "Settings saved successfully!",
        msgType: "success",
      });
      resetRestaurant(undefined, { keepValues: true });
    },
    (error) => {
      setMsgAlert({
        open: true,
        message: error.message || "Failed to save settings",
        msgType: "error",
      });
    }
  );

  // Reset forms when data is loaded
  useEffect(() => {
    // Basic settings form reset
    const basicData = basicSettings || DEFAULT_BASIC_SETTINGS;
    const basicFormData = {
      isEnabled: basicData.isEnabled || false,
      type: basicData.type || BOOKING_TYPES.CLASSIC,
    };
    resetBasic(basicFormData);

    // Restaurant settings form reset
    const restaurantData = settings?.restaurantSetting?.[0] || settings?.data || settings;
    const restaurantFormData = {
      deposit: restaurantData?.deposit || DEFAULT_RESTAURANT_SETTINGS.deposit,
      restaurantEmails: restaurantData?.restaurantEmails || DEFAULT_RESTAURANT_SETTINGS.restaurantEmails,
      restaurantPhones: restaurantData?.restaurantPhones || DEFAULT_RESTAURANT_SETTINGS.restaurantPhones,
      maxCoverPerBooking: restaurantData?.maxCoverPerBooking || DEFAULT_RESTAURANT_SETTINGS.maxCoverPerBooking,
      standardSeatingTimeMinutes: restaurantData?.standardSeatingTimeMinutes || DEFAULT_RESTAURANT_SETTINGS.standardSeatingTimeMinutes,
      bookingIntervalMinutes: restaurantData?.bookingIntervalMinutes || DEFAULT_RESTAURANT_SETTINGS.bookingIntervalMinutes,
      termsLink: restaurantData?.termsLink || DEFAULT_RESTAURANT_SETTINGS.termsLink,
      bookingAvailability: restaurantData?.bookingAvailability || DEFAULT_RESTAURANT_SETTINGS.bookingAvailability,
    };
    resetRestaurant(restaurantFormData);
  }, [basicSettings, settings, resetBasic, resetRestaurant]);

  // Handle settings error
  useEffect(() => {
    if (settingsError) {
      setMsgAlert({
        open: true,
        message: settingsError.message || "Failed to load settings",
        msgType: "error",
      });
    }
  }, [settingsError]);

  // Handle basic settings form submission
  const onSubmitBasic = (data) => {
    const basicData = {
      isEnabled: data.isEnabled,
      type: data.type,
    };

    updateBasicSettings({
      restaurantId,
      data: basicData,
    });
  };

  // Handle restaurant settings form submission
  const onSubmitRestaurant = (data) => {
    // Convert string values to numbers for numeric fields, handle undefined/null values
    const processedData = {
      ...data,
      deposit: data.deposit?.map(deposit => ({
        ...deposit,
        priceConfiguration: deposit.priceConfiguration?.map(config => ({
          ...config,
          minCover: config.minCover ? Number(config.minCover) : 0,
          maxCover: config.maxCover ? Number(config.maxCover) : 0,
          price: config.price ? Number(config.price) : 0,
        })) || [],
      })) || [],
    };

    const restaurantData = {
      _id_restaurant: restaurantId, // This will be extracted by the API function and used in headers, then removed from data
      deposit: processedData.deposit,
      restaurantEmails: processedData.restaurantEmails || [],
      restaurantPhones: processedData.restaurantPhones || [],
      maxCoverPerBooking: processedData.maxCoverPerBooking ? Number(processedData.maxCoverPerBooking) : 0,
      standardSeatingTimeMinutes: processedData.standardSeatingTimeMinutes ? Number(processedData.standardSeatingTimeMinutes) : 0,
      bookingIntervalMinutes: processedData.bookingIntervalMinutes ? Number(processedData.bookingIntervalMinutes) : 0,
      termsLink: processedData.termsLink || "",
      bookingAvailability: (processedData.bookingAvailability || []).map(availability => ({
        ...availability,
        totalCovers: 0,
        availableCovers: 0,
      })),
    };

    // Save restaurant settings
    createSettings(restaurantData);
  };

  const handleCloseAlert = () => {
    setMsgAlert({ open: false, message: "", msgType: "error" });
  };

  const isLoading = isLoadingSettings || isCreating || isUpdatingBasic;

  return (
    <Container
      page={"restaurants"}
      searchVal={""}
      onSearch={() => null}
      searchLabel=""
    >
      {isLoading ? (
        <Loader height={{ height: window.innerHeight - 150 }} />
      ) : (
        <Grid container item xs={12}>
          <Grid item xs={12} mb={2}>
            <RestaurantHeader
              title={"Table Booking Restaurant Settings"}
              onBackBtnClick={() => navigate(`/restaurant/${restaurantId}`)}
            />
          </Grid>
          <Grid item xs={12} className={styles.mainView} px={3} pt={2}>
            {/* Basic Settings Toggle */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12}>
                <Typography variant="h6" className={styles.sectionTitle} sx={{ mb: 2 }}>
                  Basic Settings
                </Typography>
                <form onSubmit={handleBasicSubmit(onSubmitBasic)}>
                  <TableBookingToggle
                    control={basicControl}
                    watch={watchBasic}
                    onNavigateToSettings={null} // No navigation needed since we're on this page
                    isLoading={isLoading}
                    styles={styles}
                  />
                  <Box sx={{ mt: 2 }}>
                    <Button
                      type="submit"
                      disabled={isUpdatingBasic}
                      className={styles.saveBtn}
                      sx={{
                        backgroundColor: "#000000 !important",
                        color: "#ffffff !important",
                        opacity: isUpdatingBasic ? 0.6 : 1,
                        "&:hover": {
                          backgroundColor: "#333333 !important",
                        },
                        "&:disabled": {
                          backgroundColor: "#000000 !important",
                          color: "#ffffff !important",
                        },
                      }}
                    >
                      {isUpdatingBasic ? "Saving..." : "Save Basic Settings"}
                    </Button>
                  </Box>
                </form>
              </Grid>
            </Grid>

            {watchBasic('isEnabled') && (
              <form onSubmit={handleRestaurantSubmit(onSubmitRestaurant)}>
                <Grid container spacing={3}>
                {/* Basic Configuration Section - Show for both classic and supreme */}
                <Grid item xs={12}>
                  <Typography className={styles.sectionTitle}>
                    Basic Configuration
                  </Typography>
                  
                  {/* Max Cover Per Booking */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Controller
                        name="maxCoverPerBooking"
                        control={restaurantControl}
                        render={({ field }) => (
                          <SettingTextField
                            value={field.value}
                            setValue={field.onChange}
                            page="tableBookingSettings"
                            placeholder="Max Cover Per Booking"
                            type="number"
                            variant="filled"
                            label="Max Cover Per Booking"
                            error={restaurantErrors.maxCoverPerBooking?.message}
                          />
                        )}
                      />
                    </Grid>
                    
                    {/* Standard Seating Time */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Controller
                        name="standardSeatingTimeMinutes"
                        control={restaurantControl}
                        render={({ field }) => (
                          <SettingTextField
                            value={field.value}
                            setValue={field.onChange}
                            page="tableBookingSettings"
                            placeholder="Standard Seating Time (Minutes)"
                            type="number"
                            variant="filled"
                            label="Standard Seating Time (Minutes)"
                            error={restaurantErrors.standardSeatingTimeMinutes?.message}
                          />
                        )}
                      />
                    </Grid>
                    
                    {/* Booking Interval */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Controller
                        name="bookingIntervalMinutes"
                        control={restaurantControl}
                        render={({ field }) => (
                          <SettingTextField
                            value={field.value}
                            setValue={field.onChange}
                            page="tableBookingSettings"
                            placeholder="Booking Interval (Minutes)"
                            type="number"
                            variant="filled"
                            label="Booking Interval (Minutes)"
                            error={restaurantErrors.bookingIntervalMinutes?.message}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  
                  {/* Terms Link */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                      <Controller
                        name="termsLink"
                        control={restaurantControl}
                        render={({ field }) => (
                          <SettingTextField
                            value={field.value || ""}
                            setValue={field.onChange}
                            page="tableBookingSettings"
                            placeholder="Terms and Conditions Link (Optional)"
                            type="url"
                            variant="filled"
                            label="Terms Link"
                            error={restaurantErrors.termsLink?.message}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Restaurant Contact Section - Show for both classic and supreme */}
                <Grid item xs={12}>
                  <Typography className={styles.sectionTitle}>
                    Restaurant Contact Information
                  </Typography>
                  
                  {/* Restaurant Emails */}
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Restaurant Emails
                  </Typography>
                  {emailFields.map((field, index) => (
                    <Grid container spacing={2} key={field.id} sx={{ mb: 1.5 }}>
                      <Grid item xs={10}>
                        <Controller
                          name={`restaurantEmails.${index}`}
                          control={restaurantControl}
                          render={({ field }) => (
                            <SettingTextField
                              value={field.value}
                              setValue={field.onChange}
                              page="tableBookingSettings"
                              placeholder="Email address"
                              type="email"
                              variant="filled"
                              error={restaurantErrors.restaurantEmails?.[index]?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          onClick={() => removeEmail(index)}
                          disabled={emailFields.length === 1}
                          variant="outlined"
                          color="error"
                          size="small"
                        >
                          Remove
                        </Button>
                      </Grid>
                    </Grid>
                  ))}
                  <Button
                    onClick={() => appendEmail("")}
                    variant="outlined"
                    size="small"
                    sx={{ mb: 3 }}
                  >
                    Add Email
                  </Button>

                  {/* Restaurant Phones */}
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Restaurant Phone Numbers
                  </Typography>
                  {phoneFields.map((field, index) => (
                    <Grid container spacing={2} key={field.id} sx={{ mb: 1.5 }}>
                      <Grid item xs={10}>
                        <Controller
                          name={`restaurantPhones.${index}`}
                          control={restaurantControl}
                          render={({ field }) => (
                            <SettingTextField
                              value={field.value}
                              setValue={field.onChange}
                              page="tableBookingSettings"
                              placeholder="Phone number"
                              type="tel"
                              variant="filled"
                              error={restaurantErrors.restaurantPhones?.[index]?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          onClick={() => removePhone(index)}
                          disabled={phoneFields.length === 1}
                          variant="outlined"
                          color="error"
                          size="small"
                        >
                          Remove
                        </Button>
                      </Grid>
                    </Grid>
                  ))}
                  <Button
                    onClick={() => appendPhone("")}
                    variant="outlined"
                    size="small"
                  >
                    Add Phone
                  </Button>
                </Grid>

                {/* Booking Availability Section - Show for both classic and supreme */}
                <Grid item xs={12}>
                  <BookingAvailabilityWindows
                    bookingAvailability={watchRestaurant('bookingAvailability') || []}
                    onBookingAvailabilityChange={(newAvailability) => {
                      resetRestaurant({
                        ...watchRestaurant(),
                        bookingAvailability: newAvailability
                      });
                    }}
                    errors={restaurantErrors}
                  />
                </Grid>

                {/* Supreme Settings - Only show for supreme type */}
                {watchBasic('type')==='supreme' && (
                  <>
                    {/* Deposit Section */}
                    <Grid item xs={12}>
                      <Typography className={styles.sectionTitle}>
                        Deposit Configuration
                      </Typography>
                      
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                        Price Configuration
                      </Typography>
                      
                      {/* Headers for Price Configuration */}
                      <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid item xs={3}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                            Min Cover
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                            Max Cover
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                            Price
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                            Action
                          </Typography>
                        </Grid>
                      </Grid>
                      
                      {priceConfigFields.map((field, index) => (
                        <Grid container spacing={2} key={field.id} sx={{ mb: 1.5 }}>
                          <Grid item xs={3}>
                            <Controller
                              name={`deposit.0.priceConfiguration.${index}.minCover`}
                              control={restaurantControl}
                              render={({ field }) => (
                                <SettingTextField
                                  value={field.value}
                                  setValue={field.onChange}
                                  page="tableBookingSettings"
                                  placeholder="Min Cover"
                                  type="number"
                                  variant="filled"
                                  error={restaurantErrors.deposit?.[0]?.priceConfiguration?.[index]?.minCover?.message}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <Controller
                              name={`deposit.0.priceConfiguration.${index}.maxCover`}
                              control={restaurantControl}
                              render={({ field }) => (
                                <SettingTextField
                                  value={field.value}
                                  setValue={field.onChange}
                                  page="tableBookingSettings"
                                  placeholder="Max Cover"
                                  type="number"
                                  variant="filled"
                                  error={restaurantErrors.deposit?.[0]?.priceConfiguration?.[index]?.maxCover?.message}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <Controller
                              name={`deposit.0.priceConfiguration.${index}.price`}
                              control={restaurantControl}
                              render={({ field }) => (
                                <SettingTextField
                                  value={field.value}
                                  setValue={field.onChange}
                                  page="tableBookingSettings"
                                  placeholder="Price"
                                  type="number"
                                  variant="filled"
                                  error={restaurantErrors.deposit?.[0]?.priceConfiguration?.[index]?.price?.message}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <Button
                              onClick={() => removePriceConfig(index)}
                              disabled={priceConfigFields.length === 1}
                              variant="outlined"
                              color="error"
                              size="small"
                            >
                              Remove
                            </Button>
                          </Grid>
                        </Grid>
                      ))}
                      <Button
                        onClick={() => appendPriceConfig({ minCover: 1, maxCover: 10, price: 100 })}
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      >
                        Add Price Configuration
                      </Button>
                    </Grid>

                  </>
                )}

                {/* Action Buttons */}
                <Grid item xs={12} marginBottom={4}>
                  <Box className={styles.btnMainView}>
                    <Button
                      type="submit"
                      disabled={isCreating}
                      className={styles.saveBtn}
                      sx={{
                        backgroundColor: "#000000 !important",
                        color: "#ffffff !important",
                        opacity: isCreating ? 0.6 : 1,
                        "&:hover": {
                          backgroundColor: "#333333 !important",
                        },
                        "&:disabled": {
                          backgroundColor: "#000000 !important",
                          color: "#ffffff !important",
                        },
                      }}
                    >
                      {isCreating ? "Saving..." : "Save Restaurant Settings"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
            )}
          </Grid>
        </Grid>
      )}
      
      {/* Alert Messages */}
      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={handleCloseAlert}
      />
    </Container>
  );
};

export default TableBookingRestaurantSettings;
